import PropTypes from 'prop-types'
import { useRef } from 'react'

import {
  useExportableChart,
  svgToPngDataUrl
} from '@aytm/app/stats/page/question-views/components/charts/lib/exportable'
import { calcBadData } from '@aytm/app/stats/page/question-views/components/lib/utils'
import { setDefaults } from '@aytm/app/stats/page/question-views/vk/lib/utils'
import { viewSharedProps } from '@aytm/app/stats/page/question-views/vk/lib/constants'
import useVanKonanState from '@aytm/app/stats/page/question-views/vk/states/useVanKonanState'

// components
import BlockWrapper from '@aytm/app/stats/page/question-views/components/BlockWrapper'
import Preloader from '@aytm/app/stats/page/question-views/components/Preloader'
import { getColorTheme } from '@aytm/app/stats/lib/stat-page'
import BadData from '@aytm/app/stats/page/question-views/vk/components/views/bad-data/BadData'
import InfographicView from '@aytm/app/stats/page/question-views/vk/components/views/infographic/InfographicView'
import DefaultView from '@aytm/app/stats/page/question-views/vk/components/views/chart/DefaultView'
import { CHART_HEIGHT } from '@aytm/app/stats/page/question-views/components/lib/constants'

const Block = ({
  sectionKey,
  id,
  type,
  components,
  palette,
  globalChartMode,
  text,
  answer,
  format,
  params,
  paramsRevision,
  isReportXpert,
  reportParams,
  isAnimated,
  onReady,
  onStateChange,
  onChartExport
}) => {
  const { period, fqPeriod, infographic, config, navTo, loading, onModelChange, onReset } = useVanKonanState({
    params,
    paramsRevision,
    format,
    components,
    answer,
    isReportXpert,
    onReady,
    onStateChange
  })

  // Headless export wiring. Declared before the bad-data early return so the hook
  // is called unconditionally; no-op on the live page (requestId null).
  const containerRef = useRef(null)

  useExportableChart({
    deps: [answer, !loading, answer !== 'loading'],
    prepareForExport: async () => {
      const svgs = Array.from(containerRef.current?.querySelectorAll('svg[data-capture-index]') || [])
      if (svgs.length === 0) throw new Error('VK: no sub-chart SVGs found (bad data or not rendered)')
      // Stable order via explicit attribute; one PNG per rendered sub-chart.
      svgs.sort((a, b) => Number(a.dataset.captureIndex) - Number(b.dataset.captureIndex))
      return Promise.all(
        svgs.map(el => {
          const w = el.width.baseVal.value || Number(el.getAttribute('width')) || 746
          const h = el.height.baseVal.value || Number(el.getAttribute('height')) || CHART_HEIGHT
          const svgString =
            '<?xml version="1.0" encoding="UTF-8" standalone="no"?>' + new XMLSerializer().serializeToString(el)
          return svgToPngDataUrl(svgString, w, h)
        })
      )
    }
  })

  const badData = calcBadData({ answer })

  const defaults = setDefaults({ answer })

  const viewProps = {
    id,
    isLoading: 'loading' === answer,
    isAnimated,
    type,
    text,
    components,
    period: parseInt(period),
    palette,
    globalChartMode,
    config,
    defaults,
    answer,
    onModelChange,
    onExport: onChartExport
  }

  return (
    <BlockWrapper containerRef={containerRef} answer={answer}>
      {badData ? (
        <BadData sectionKey={sectionKey} reportParams={reportParams} colorTheme={getColorTheme()} onReset={onReset} />
      ) : (
        <>
          {infographic ? (
            <InfographicView {...viewProps} navTo={navTo} fqPeriod={parseInt(fqPeriod)} />
          ) : (
            <DefaultView {...viewProps} />
          )}

          {loading ? <Preloader /> : null}
        </>
      )}
    </BlockWrapper>
  )
}

Block.displayName = 'Block'

Block.propTypes = {
  ...viewSharedProps,
  sectionKey: PropTypes.string,
  format: PropTypes.object,
  params: PropTypes.shape({ vk: PropTypes.oneOfType([PropTypes.object, PropTypes.string]) }),
  paramsRevision: PropTypes.number,
  isReportXpert: PropTypes.bool,
  onReady: PropTypes.func,
  onStateChange: PropTypes.func
}

export default Block
